import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { getStoreInfo } from '../../api/stores';
import { getPrompt, createPrompt, updatePrompt } from '../../api/prompts';


const StorePromptPage = () => {
    const { uuid } = useParams();
    const navigate = useNavigate();

    const [스토어명, set스토어명] = useState('');
    const [로딩중, set로딩중] = useState(true);
    const [에러, set에러] = useState(null);

    const [savedPrompt, setSavedPrompt] = useState(null); // 서버에 저장된 값
    const [draft, setDraft] = useState('');               // 편집 중인 값

    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const isDirty = draft !== (savedPrompt ?? '');
    const hasPrompt = savedPrompt !== null;

    useEffect(() => {
        Promise.all([getStoreInfo(uuid), getPrompt(uuid).catch(() => null)])
            .then(([스토어, promptData]) => {
                set스토어명(스토어?.name ?? '');
                const content = promptData?.prompt ?? null;
                setSavedPrompt(content);
                setDraft(content ?? '');
            })
            .catch(() => set에러('정보를 불러오지 못했습니다.'))
            .finally(() => set로딩중(false));
    }, [uuid]);

    const handleSave = async () => {
        if (!draft.trim() || isSaving) return;
        setIsSaving(true);
        setSaveError(null);
        try {
            if (hasPrompt) {
                await updatePrompt(uuid, draft);
            } else {
                await createPrompt(uuid, draft);
            }
            setSavedPrompt(draft);
        } catch {
            setSaveError('저장 중 오류가 발생했습니다.');
        } finally {
            setIsSaving(false);
        }
    };


    if (로딩중) return (
        <Page>
            <Body><Inner><StatusText>불러오는 중...</StatusText></Inner></Body>
        </Page>
    );

    if (에러) return (
        <Page>
            <Body><Inner><StatusText $error>{에러}</StatusText></Inner></Body>
        </Page>
    );

    return (
        <Page>
            <Body>
                <Inner>
                    <BackButton onClick={() => navigate(`/stores/${uuid}/channels`)}>
                        ← 채널 목록
                    </BackButton>

                    <TitleArea>
                        <PageBadge>프롬프트</PageBadge>
                        <PageTitle>{스토어명 || '스토어'}</PageTitle>
                        <PageDesc>
                            AI가 고객과 대화할 때 기본으로 사용하는 지침입니다.<br />
                            말투, 응대 방식, 주의사항 등을 자유롭게 작성하세요.
                        </PageDesc>
                    </TitleArea>

                    <EditorCard>
                        <EditorHeader>
                            <EditorLabel>프롬프트 내용</EditorLabel>
                            {hasPrompt && !isDirty && (
                                <SavedBadge>저장됨</SavedBadge>
                            )}
                            {isDirty && (
                                <UnsavedBadge>미저장</UnsavedBadge>
                            )}
                        </EditorHeader>

                        <PromptTextarea
                            placeholder={`예시:\n안녕하세요, 저는 ${스토어명 || '매장'} AI 상담원입니다.\n고객의 예약 문의에 친절하고 정확하게 응대해주세요.\n- 예약 가능 시간을 먼저 안내하세요.\n- 모르는 내용은 직접 확인 후 안내한다고 전달하세요.`}
                            value={draft}
                            onChange={(e) => { setDraft(e.target.value); setSaveError(null); }}
                            rows={14}
                        />

                        <CharCount $warn={draft.length > 2000}>
                            {draft.length.toLocaleString()} 자
                        </CharCount>

                        {saveError && <SaveError>{saveError}</SaveError>}

                        <ActionRow>
                            <SaveButton
                                onClick={handleSave}
                                disabled={!draft.trim() || !isDirty || isSaving}
                            >
                                {isSaving ? '저장 중...' : hasPrompt ? '수정 저장' : '프롬프트 저장'}
                            </SaveButton>
                        </ActionRow>
                    </EditorCard>

                    {!hasPrompt && !draft && (
                        <EmptyGuide>
                            아직 등록된 프롬프트가 없습니다. 위에 내용을 작성하고 저장해보세요.
                        </EmptyGuide>
                    )}
                </Inner>
            </Body>
        </Page>
    );
};


const Page = styled.div`
    width: 100%;
    min-height: 100dvh;
    background: #f7f8fa;
    font-family: Pretendard, -apple-system, sans-serif;
`;

const Body = styled.div`
    padding-top: 80px;
    padding-bottom: 60px;
    display: flex;
    justify-content: center;
`;

const Inner = styled.div`
    width: 100%;
    max-width: 640px;
    padding: 0 16px;
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

const BackButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #aaaaaa;
    cursor: pointer;
    align-self: flex-start;
    &:hover { color: #333333; }
`;

const TitleArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const PageBadge = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: #487BFD;
    letter-spacing: 0.1em;
    text-transform: uppercase;
`;

const PageTitle = styled.h1`
    font-size: 24px;
    font-weight: 800;
    color: #111111;
    letter-spacing: -0.02em;
    margin: 0;
`;

const PageDesc = styled.p`
    font-size: 14px;
    color: #999999;
    line-height: 1.65;
    margin: 0;
`;

const EditorCard = styled.div`
    background: #ffffff;
    border: 1.5px solid #eeeeee;
    border-radius: 16px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const EditorHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const EditorLabel = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: #333333;
    flex: 1;
`;

const SavedBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: #10B981;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    border-radius: 4px;
    padding: 2px 8px;
`;

const UnsavedBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: #f59e0b;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 4px;
    padding: 2px 8px;
`;

const PromptTextarea = styled.textarea`
    width: 100%;
    box-sizing: border-box;
    background: #f7f8fa;
    border: 1px solid #e8e8e8;
    border-radius: 10px;
    padding: 14px 16px;
    font-family: Pretendard;
    font-size: 14px;
    color: #111111;
    line-height: 1.75;
    outline: none;
    resize: vertical;
    transition: border-color 0.15s;

    &::placeholder { color: #c0c0c0; }
    &:focus { border-color: #487BFD; background: #ffffff; }
`;

const CharCount = styled.span`
    font-size: 12px;
    color: ${({ $warn }) => $warn ? '#f59e0b' : '#cccccc'};
    text-align: right;
`;

const SaveError = styled.span`
    font-size: 12px;
    color: #EF4444;
`;

const ActionRow = styled.div`
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    margin-top: 4px;
`;


const SaveButton = styled.button`
    background: #487BFD;
    border: none;
    border-radius: 8px;
    padding: 9px 22px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: opacity 0.15s;

    &:hover:not(:disabled) { opacity: 0.85; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const EmptyGuide = styled.p`
    font-size: 13px;
    color: #bbbbbb;
    text-align: center;
    margin: 0;
    padding: 8px 0 16px;
`;

const StatusText = styled.span`
    font-family: Pretendard;
    font-size: 15px;
    color: ${({ $error }) => $error ? '#EF4444' : '#888888'};
`;


export default StorePromptPage;
